// pages/login.js
import { useRef, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, persistAuthProxySession, setAuthProxyEnabled } from '../firebase-client';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import PasswordInput from '../components/forms/formComponents/PasswordInput';
import { collectAuthNetworkDiagnostics } from '../utils/authNetworkDiagnostics';
import { reportCaughtError } from '../utils/clientErrorReporting';
import {
  buildLoginAttemptTelemetry,
  formatLoginRetryDelay,
  getLoginThrottle,
  recordLoginFailure,
  recordLoginSuccess,
} from '../utils/loginAttemptSecurity';

function resolveSafeNextPath(rawNext, fallback = '/dashboard') {
  if (typeof rawNext !== 'string' || rawNext.trim() === '') return fallback;
  if (!rawNext.startsWith('/')) return fallback;
  if (rawNext.startsWith('//')) return fallback;
  return rawNext;
}

function isInvalidCredentialError(error) {
  return [
    'auth/invalid-credential',
    'auth/wrong-password',
    'auth/user-not-found',
  ].includes(error?.code);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const nextPath = resolveSafeNextPath(params.get('next'), '/dashboard');
  const encodedNextPath = encodeURIComponent(nextPath);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);
    const normalizedEmail = email.trim().toLowerCase();
    const throttle = getLoginThrottle(normalizedEmail);
    if (throttle.blocked) {
      setError(`Too many unsuccessful login attempts from this device. Please try again in ${formatLoginRetryDelay(throttle.retryAfterSeconds)}.`);
      return;
    }

    setIsSubmitting(true);
    try {
      persistAuthProxySession(false);
      setAuthProxyEnabled(false);
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      recordLoginSuccess(normalizedEmail);
      navigate(nextPath);
    } catch (err) {
      if (isInvalidCredentialError(err)) {
        const failureStatus = recordLoginFailure(normalizedEmail);
        const loginAttemptTelemetry = await buildLoginAttemptTelemetry(normalizedEmail);
        setEmail('');
        setPassword('');
        setError(failureStatus.locked
          ? `Too many unsuccessful login attempts from this device. Please try again in ${formatLoginRetryDelay(failureStatus.retryAfterSeconds)}.`
          : 'We could not sign you in with those details. Please check your email and password, then try again.');
        window.setTimeout(() => emailInputRef.current?.focus(), 0);
        reportCaughtError(err, {
          source: 'login.emailPassword',
          severity: 'warning',
          bypassDedupe: true,
          context: {
            nextPath,
            firebaseMessage: err?.customData?.message || '',
            loginFailureCount: failureStatus.failureCount,
            loginAttemptAccountHash: loginAttemptTelemetry.accountHash,
            loginAttemptAccountHashVersion: loginAttemptTelemetry.accountHashVersion,
            loginAttemptAccountLabel: loginAttemptTelemetry.accountLabel,
            loginAttemptAccountDomain: loginAttemptTelemetry.accountDomain,
            loginAttemptAccountLocalLength: loginAttemptTelemetry.accountLocalLength,
          },
        });
        return;
      }

      setError('Something went wrong while signing you in. Please try again.');

      if (err?.code === 'auth/network-request-failed') {
        collectAuthNetworkDiagnostics()
          .then((authNetworkDiagnostics) => reportCaughtError(new Error('Firebase Auth network diagnostics'), {
            source: 'login.emailPassword.networkDiagnostics',
            severity: 'info',
            bypassDedupe: true,
            context: {
              nextPath,
              authNetworkDiagnostics,
            },
          }))
          .catch((diagnosticError) => reportCaughtError(diagnosticError, {
            source: 'login.emailPassword.networkDiagnosticsFailed',
            severity: 'warning',
            bypassDedupe: true,
            context: {
              nextPath,
            },
          }));

        if (setAuthProxyEnabled(true)) {
          try {
            await signInWithEmailAndPassword(auth, normalizedEmail, password);
            persistAuthProxySession(true);
            reportCaughtError(new Error('Firebase Auth proxy retry succeeded'), {
              source: 'login.emailPassword.proxyRetry',
              severity: 'info',
              bypassDedupe: true,
              context: {
                nextPath,
                proxyRetrySucceeded: true,
              },
            });
            navigate(nextPath);
            return;
          } catch (proxyErr) {
            reportCaughtError(proxyErr, {
              source: 'login.emailPassword.proxyRetry',
              severity: 'warning',
              bypassDedupe: true,
              context: {
                nextPath,
                originalCode: err?.code || '',
              },
            });
          } finally {
            if (!auth.currentUser) {
              persistAuthProxySession(false);
              setAuthProxyEnabled(false);
            }
          }
        } else {
          reportCaughtError(err, {
            source: 'login.emailPassword',
            severity: 'warning',
            bypassDedupe: true,
            context: {
              nextPath,
              firebaseMessage: err?.customData?.message || '',
              proxyRetryAvailable: false,
            },
          });
        }
      } else {
        reportCaughtError(err, {
          source: 'login.emailPassword',
          severity: 'warning',
          bypassDedupe: true,
          context: {
            nextPath,
            firebaseMessage: err?.customData?.message || '',
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      <section className="section">
        <div className="card" style={{ maxWidth: '500px', marginInline: 'auto' }}>
          <h1 className="section-title">Login</h1>

          <form onSubmit={handleLogin} className="flex-column" style={{ gap: '1rem' }} autoComplete="on">
            <div className="flex-column" style={{ gap: '0.35rem' }}>
              <label htmlFor="login-email" style={{ fontWeight: 600 }}>Email</label>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="email"
                placeholder="Email"
                value={email}
                ref={emailInputRef}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
              />
            </div>

            <PasswordInput
              inputId="login-password"
              label="Password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button type="submit" className="button" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Login'}
            </button>
            {error && <p style={{ color: 'red', margin: 0 }}>{error}</p>}
          </form>

          <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            <Link to={`/register?next=${encodedNextPath}`}>Don’t have an account? Sign up here</Link>.<br></br> <Link to="/reset-password">Forgotten your password? Reset it</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
